import { useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  ShoppingCart,
  CreditCard,
  Maximize2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { addItems } from "../../slice/cartSlice";
import { PageLoader } from "../../helpers/Loader";

const ProductDetailView = ({ product, loading, error }) => {
  const [quantity, setQuantity] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isHovering, setIsHovering] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showZoomOverlay, setShowZoomOverlay] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const allImages = useMemo(() => {
    return [product?.product?.image1].filter(Boolean);
  }, [product]);

  const handleImageClick = () => {
    if (isHovering) {
      setZoomLevel((prevZoom) => {
        const newZoom = prevZoom + 0.5;
        return newZoom > 3 ? 1 : newZoom;
      });
    }
  };

  const handlebrand = (brandname) => {
    navigate(`/shop/brand/${brandname}`);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) =>
      prevIndex === 0 ? allImages?.length - 1 : prevIndex - 1
    );
  };

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % allImages.length);
  };

  const currentPrice = useMemo(() => {
    let price = product?.price;
    return price * quantity;
  }, [quantity, product]);

  const toggleZoomOverlay = () => {
    setShowZoomOverlay(!showZoomOverlay);
    setZoomLevel(1);
  };

  const addToCart = (productItem, qty, buyNow) => {
    const newItem = {
      ...productItem,
      quantity: qty,
      buyNow: buyNow,
    };
    dispatch(addItems(newItem));
  };

  if (error) {
    return (
      <div className="w-full min-h-[60vh] flex items-center justify-center bg-white">
        <div className="max-w-md mx-4 p-6 rounded-lg border border-red-200 bg-red-50 text-center">
          <p className="text-red-700 font-medium">{error}</p>
        </div>
      </div>
    );
  }

  if (loading || !product) {
    return <PageLoader message="Loading product..." />;
  }

  return (
    <div
      className="w-full bg-white p-0 md:p-4"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-1/2 p-4">
          <div className="relative overflow-hidden rounded-lg bg-gray-50">
            <div
              className="relative w-full aspect-square overflow-hidden cursor-zoom-in"
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setZoomLevel(1);
              }}
              onClick={handleImageClick}
            >
              <img
                src={allImages[currentImageIndex]}
                alt={product?.name}
                className="w-full h-full object-contain transition-transform duration-300"
                style={{
                  transform: `scale(${zoomLevel})`,
                }}
              />
              <button
                className="absolute top-4 right-4 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleZoomOverlay();
                }}
              >
                <Maximize2 size={18} />
              </button>
            </div>

            <button
              className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
              onClick={handlePrevImage}
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md hover:bg-gray-100 transition-colors"
              onClick={handleNextImage}
            >
              <ChevronRight size={20} />
            </button>
          </div>

          <div className="flex justify-center mt-4 gap-2 overflow-x-auto">
            {allImages.map((img, index) => (
              <button
                key={index}
                className={`flex-shrink-0 border-2 rounded-md overflow-hidden transition-all ${
                  currentImageIndex === index
                    ? "border-indigo-600 shadow-lg"
                    : "border-transparent"
                }`}
                onClick={() => setCurrentImageIndex(index)}
              >
                <img
                  src={img}
                  alt={`Thumbnail ${index + 1}`}
                  className="w-16 h-16 object-cover"
                />
              </button>
            ))}
          </div>
        </div>

        <div className="w-full md:w-1/2 p-4 md:pl-8">
          <div className="mb-6">
            <div className=" items-start">
              <h1 className="text-2xl font-bold text-gray-900">
                {product?.product?.name}
              </h1>
              <p
                className="text-gray-900"
                dangerouslySetInnerHTML={{ __html: product?.product?.htmlDesc }}
              />
            </div>

            <div className="mt-2">
              <div className="inline-block bg-green-100 text-green-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
                In Stock
              </div>
              {product?.product?.brand?.name && (
                <span className="text-sm text-gray-500 ml-2">
                  By{" "}
                  <span
                    className="text-sm text-blue-500 cursor-pointer"
                    onClick={() => handlebrand(product?.product?.brand?.slug)}
                  >
                    {product?.product?.brand?.name}
                  </span>
                </span>
              )}
            </div>
          </div>

          <div className="mb-6">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-bold text-gray-900">
                ₹{currentPrice?.toFixed(2)}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Inclusive of all taxes</p>
          </div>

          <div className="py-4 border-t border-b border-gray-200 mb-6">
            <div className=" w-1/2">
              <div className=" flex justify-between items-center">
                <p className="text-sm text-gray-500">Brand</p>
                <p
                  className="font-medium cursor-pointer"
                  onClick={() => handlebrand(product?.product?.brand?.slug)}
                >
                  {product?.product?.brand?.name || "N/A"}
                </p>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label
              htmlFor="quantity"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Quantity
            </label>
            <div className="flex w-[25%] h-10 border border-gray-300 rounded-lg overflow-hidden">
              <button
                className="flex-none w-10 flex items-center justify-center hover:bg-gray-100 transition-colors"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
              >
                <Minus size={16} />
              </button>
              <input
                type="text"
                className="text-center bg-white border-x border-gray-300 outline-none w-[40%]"
                value={quantity}
                readOnly
              />
              <button
                className="flex-none w-10 flex items-center justify-center hover:bg-gray-100 transition-colors"
                onClick={() => setQuantity((q) => q + 1)}
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              className="flex-1 py-3 px-4 flex items-center justify-center gap-2 bg-white border border-gray-300 rounded-lg text-gray-800 font-medium hover:bg-gray-50 transition-colors"
              onClick={() => addToCart(product, quantity, false)}
            >
              <ShoppingCart size={20} />
              <span>Add to Cart</span>
            </button>
            <button
              className="flex-1 py-3 px-4 flex items-center justify-center gap-2 bg-black border border-black rounded-lg text-white font-medium hover:bg-black transition-colors"
              onClick={() => addToCart(product, quantity, true)}
            >
              <CreditCard size={20} />
              <span>Buy Now</span>
            </button>
          </div>
        </div>
      </div>

      {showZoomOverlay && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
          onClick={toggleZoomOverlay}
        >
          <button
            className="absolute top-4 right-4 text-white bg-black bg-opacity-50 p-2 rounded-full"
            onClick={toggleZoomOverlay}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-x"
            >
              <path d="M18 6 6 18"></path>
              <path d="m6 6 12 12"></path>
            </svg>
          </button>
          <img
            src={allImages[currentImageIndex]}
            alt={product?.product?.name}
            className="max-w-full max-h-full object-contain"
          />
          <button
            className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-3 rounded-full text-white"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevImage();
            }}
          >
            <ChevronLeft size={24} />
          </button>
          <button
            className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 p-3 rounded-full text-white"
            onClick={(e) => {
              e.stopPropagation();
              handleNextImage();
            }}
          >
            <ChevronRight size={24} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ProductDetailView;
